import {
	Link,
	useNavigate,
	useRouter,
	useSearch,
} from '@tanstack/react-router';
import { CurrencyType, type ShipmentListDTO } from '@uturn/api/v1';
import { GetShipmentsParams } from '@uturn/api/v3';
import { DraftedShipments as DraftedShipmentsEmptyState } from '@uturn/design-tokens/empty-states';
import {
	RequirementBadges,
	ShipmentPage,
} from '@uturn/portal/modules/shipments/components';
import { useShipmentsSearchStore } from '@uturn/portal/store/shipments/shipments-search';
import { formatPrice } from '@uturn/portal/utils';
import { Button, EmptyState } from '@uturn/ui-kit';
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { cleanFilterParams } from '../../components/filters/utils-filter-params';
import { ShipmentGroupDetailContext } from '../../providers/shipment-group-detail';

function DraftedShipmentEmptyState() {
	const { t } = useTranslation();
	return (
		<EmptyState>
			<DraftedShipmentsEmptyState className="md:h-80" />
			<h2 className="text-secondary-700 text-2xl font-medium leading-7">
				{t('pages.shipments.drafted.emptyState.title', 'To the drawing board!')}
			</h2>
			<p className="text-secondary-600 text-lg leading-6">
				{t(
					'pages.shipments.drafted.emptyState.description',
					'Here your drafts will be stored. You can publish them to the market or keep them as templates to use for future shipments.',
				)}
			</p>
			<div>
				<Link to="/shipments/add">
					<Button>{t('pages.shipments.actions.start', 'Get started')}</Button>
				</Link>
			</div>
		</EmptyState>
	);
}

export function DraftedGroupShipments() {
	const { t } = useTranslation();
	const { groupName } = useContext(ShipmentGroupDetailContext);
	const { defaultFilters, setShipmentsSearchFilters } =
		useShipmentsSearchStore();

	const router = useRouter();
	const navigate = useNavigate({ from: '/shipments/groups/$groupId/drafted' });
	const search = useSearch({
		from: '/_protected/shipments/groups/$groupId/drafted',
	});

	const shipmentDetails = (shipment: ShipmentListDTO) => [
		{
			label: t('pages.shipments.details.reference'),
			value: shipment.shipperReference,
			visibleOnMultiSelect: true,
			enableCopyToClipboard: true,
		},
		{
			label: t('pages.shipments.details.containerNumber'),
			value: shipment.containerNumber,
			visibleOnMultiSelect: true,
			enableCopyToClipboard: true,
		},
		{
			label: t('pages.shipments.details.price'),
			value: formatPrice(
				shipment.priceIncludingShipmentFee?.quantity ?? 0,
				shipment.priceIncludingShipmentFee?.currencyCode ?? CurrencyType.EUR,
			),
			visibleOnMultiSelect: true,
			hasInfoIcon: true,
		},
		{
			label: t('pages.shipments.details.requirements'),
			value: shipment.requirements && (
				<RequirementBadges requirements={shipment.requirements} />
			),
		},
	];

	return (
		<>
			<Helmet
				title={t(
					'pages.shipments.groups.drafted.title',
					'{{groupName}} - Drafted shipments',
					{
						groupName,
					},
				).toString()}
			/>
			<ShipmentPage
				enableGroupFilter={false}
				EmptyState={DraftedShipmentEmptyState}
				details={shipmentDetails}
				search={{
					...search,
					page: search.page.toString(),
					size: search.size.toString(),
				}}
				filterOnChange={(filters) => {
					if (filters === null || Object.keys(filters).length === 0) {
						return router.invalidate();
					}

					const newFilters: GetShipmentsParams = {
						...defaultFilters,
						...cleanFilterParams(filters),
					};

					setShipmentsSearchFilters(newFilters);

					navigate({
						search: {
							...newFilters,
							page: Number(newFilters.page),
							size: Number(newFilters.size),
						},
					});
				}}
			/>
		</>
	);
}
